import type { ErrorReporter } from '../common/ErrorReporter';
import type DB from '../db/DB';
import type { AirtableClient } from '../utils/Airtable';
import { delay } from '../utils/AsyncUtils';
import { duration } from '../utils/Utils';

const AIRTABLE_DB_PREFIX = 'airtable_';
const CACHE_EXPIRES_IN_MS = duration({ minutes: 15 });
const LOCK_EXPIRES_IN_MS = duration({ minutes: 1 });

export class ServerReplicantAirtable {
    private cache: {
        [tableName: string]: { items: { [key: string]: unknown }[] | null; expiresAt: number };
    } = {};

    constructor(
        private opts: {
            airtable: AirtableClient;
            dateNow: () => number;
            db: DB<any>;

            /** @default 1_000 */
            dbRetryBackoffMs?: number;

            errorReporter?: ErrorReporter;
        },
    ) {}

    async getTable(tableName: string): Promise<{ [key: string]: unknown }[] | null> {
        try {
            if (this.isCacheExpired(tableName)) {
                // Local cache is expired, try updating from DB:
                await this.updateCacheFromDB(tableName);

                if (this.isCacheExpired(tableName)) {
                    // DB cache is also expired, try updating from API:
                    const acquiredLock = await this.updateCacheFromAPI(tableName);

                    // Another client is already updating DB cache, retry DB read after a delay:
                    if (!acquiredLock) {
                        const MAX_RETRIES = 5;

                        for (let i = 0; i < MAX_RETRIES; i++) {
                            await delay((this.opts.dbRetryBackoffMs ?? 1_000) * i);

                            const updatedLocalCache = await this.updateCacheFromDB(tableName, { consistentRead: true });

                            if (updatedLocalCache) {
                                break;
                            }
                        }
                    }
                }
            }
        } catch (error: any) {
            await this.opts.errorReporter?.captureException(error);
        }

        return this.cache[tableName]?.items ?? null;
    }

    private isCacheExpired(tableName: string): boolean {
        const tableCache = this.cache[tableName];
        return !tableCache || tableCache.expiresAt < this.opts.dateNow();
    }

    private async updateCacheFromDB(tableName: string, opts?: { consistentRead?: boolean }): Promise<boolean> {
        const dbItem = await this.opts.db.internalKvStore.fetch(AIRTABLE_DB_PREFIX + tableName, {
            consistentRead: opts?.consistentRead,
        });

        if (dbItem) {
            this.cache[tableName] = JSON.parse(dbItem);

            return true;
        }

        return false;
    }

    private async updateCacheFromAPI(tableName: string): Promise<boolean> {
        // Extend cache expiration by lock duration to prevent repeated lock acquire attempts:
        this.cache[tableName] = {
            items: this.cache[tableName]?.items ?? null,
            expiresAt: this.opts.dateNow() + LOCK_EXPIRES_IN_MS,
        };

        const acquiredLock = await this.opts.db.lock.acquireTimestampLock(
            AIRTABLE_DB_PREFIX + tableName,
            this.opts.dateNow(),
            LOCK_EXPIRES_IN_MS,
        );

        if (!acquiredLock) {
            return false; // Another consumer is already updating cache
        }

        const apiItems = await this.opts.airtable.getTable(tableName);

        const cacheValue = { items: apiItems, expiresAt: this.opts.dateNow() + CACHE_EXPIRES_IN_MS };
        this.cache[tableName] = cacheValue;

        await this.opts.db.internalKvStore.write(AIRTABLE_DB_PREFIX + tableName, JSON.stringify(cacheValue));

        return true;
    }
}
